import { type FormEvent, useState } from 'react';

import { LibraryPicker } from '@/components/library-picker';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useMe } from '@/lib/api-hooks';
import { type Me, updateMe } from '@/lib/me';
import type { MediaRef } from '@/lib/media';
import { invalidateResource } from '@/lib/resource';

interface ProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** The member's own profile (name + picture) in a dialog. */
export function ProfileDialog({ open, onOpenChange }: ProfileDialogProps) {
  const { data: me } = useMe();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        {me ? (
          <ProfileForm
            key={`${me.id}:${me.name}:${me.picture}`}
            me={me}
            onClose={() => onOpenChange(false)}
          />
        ) : (
          <p className="py-8 text-center text-muted-foreground text-sm">Loading profile…</p>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ProfileForm({ me, onClose }: { me: Me; onClose: () => void }) {
  const [name, setName] = useState(me.name ?? '');
  const [avatar, setAvatar] = useState<MediaRef | null>(
    me.picture ? { id: me.picture, url: me.picture } : null,
  );
  const [saving, setSaving] = useState(false);

  const trimmed = name.trim();
  const picture = avatar?.url ?? null;
  const dirty =
    trimmed.length > 0 && (trimmed !== (me.name ?? '') || picture !== (me.picture ?? null));

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      await updateMe({ name: trimmed, picture: picture ?? '' });
      invalidateResource('me');
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="grid gap-4" onSubmit={handleSubmit}>
      <DialogHeader>
        <DialogTitle>Your profile</DialogTitle>
        <DialogDescription>Update how you appear in the CMS.</DialogDescription>
      </DialogHeader>

      <div className="grid gap-2">
        <Label>Picture</Label>
        <LibraryPicker value={avatar} onChange={setAvatar} types={['image']} />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="profile-name">Display name</Label>
        <Input
          id="profile-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Your name"
        />
      </div>

      <div className="grid gap-1">
        <Label>Email</Label>
        <p className="text-muted-foreground text-sm">{me.email}</p>
      </div>

      <DialogFooter className="pt-2">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={!dirty || saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </DialogFooter>
    </form>
  );
}
